class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.addEventListener('click', this.handleDrawerClick.bind(this));
    this.setHeaderCartIconAccessibility();

    window.addEventListener('pageshow', (event) => {
      if (event.persisted) {
        const checkoutButton = this.querySelector('#CartDrawer-Checkout');
        if (checkoutButton) checkoutButton.disabled = false;
      }
    });
  }

  async handleDrawerClick(event) {
    const checkoutButton = event.target.closest('#CartDrawer-Checkout');
    if (!checkoutButton || checkoutButton.disabled) return;

    event.preventDefault();
    checkoutButton.disabled = true;

    try {
      const cart = await fetch(`${routes.cart_url}.json`).then((response) => {
        if (!response.ok) throw new Error('Failed to fetch cart');
        return response.json();
      });

      // Clear leftover card message so minicart checkout does not carry an old cart attribute
      if (cart.attributes?.['Mensagem do cartão']) {
        await fetch(`${routes.cart_update_url}`, {
          ...fetchConfig(),
          body: JSON.stringify({
            attributes: { 'Mensagem do cartão': '' },
          }),
        });
      }

      const codes = getApplicableDiscountCodes(cart);

      if (codes.length > 0) {
        await fetch(`${routes.cart_update_url}`, {
          ...fetchConfig(),
          body: JSON.stringify({ discount: codes.join(',') }),
        });

        const discountSegment = codes.map((code) => encodeURIComponent(code)).join(',');
        const redirect = encodeURIComponent('/checkout');
        window.location.href = `${window.shopUrl}/discount/${discountSegment}?redirect=${redirect}`;
        return;
      }

      window.location.href = '/checkout';
    } catch (error) {
      console.error(error);
      checkoutButton.disabled = false;
    }
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (this.classList.contains('active')) return;
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true },
    );

    document.body.classList.add('overflow-hidden');

    // cart-drawer-items is a CartItems subclass that extends createViewEventElement.
    // Its `view-event-trigger="manual"` skips auto-dispatch on connect; we fire
    // it here when the drawer opens, with `context: 'dialog'` from the payload attribute.
    this.querySelector('cart-drawer-items')?.dispatchViewEvent();
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

function getApplicableDiscountCodes(cart) {
  const codes = [];

  (cart?.discount_codes || []).forEach((entry) => {
    if (typeof entry === 'string') {
      codes.push(entry);
      return;
    }

    if (entry?.code && entry.applicable !== false) {
      codes.push(entry.code);
    }
  });

  return codes;
}

class CartDrawerCoupon extends HTMLElement {
  constructor() {
    super();

    this.handleToggle = this.handleToggle.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleRemove = this.handleRemove.bind(this);
    this.handleTriggerKeydown = this.handleTriggerKeydown.bind(this);
  }

  connectedCallback() {
    this.trigger = this.querySelector('.cart-drawer__footer-cupom-trigger');
    this.form = this.querySelector('.cart-drawer__coupon-form');
    this.applied = this.querySelector('.cart-drawer__coupon-applied');
    this.input = this.querySelector('.cart-drawer__coupon-input');
    this.submitBtn = this.querySelector('.cart-drawer__coupon-submit');
    this.removeBtn = this.querySelector('.cart-drawer__coupon-remove');
    this.errorEl = this.querySelector('.cart-drawer__coupon-error');
    this.appliedText = this.querySelector('.cart-drawer__coupon-applied-text');

    this.trigger?.addEventListener('click', this.handleToggle);
    this.trigger?.addEventListener('keydown', this.handleTriggerKeydown);
    this.submitBtn?.addEventListener('click', this.handleSubmit);
    this.removeBtn?.addEventListener('click', this.handleRemove);
    this.input?.addEventListener('keydown', (event) => {
      if (event.code === 'Enter') {
        event.preventDefault();
        this.handleSubmit();
      }
    });

    this.syncWithCart();
  }

  disconnectedCallback() {
    this.trigger?.removeEventListener('click', this.handleToggle);
    this.trigger?.removeEventListener('keydown', this.handleTriggerKeydown);
    this.submitBtn?.removeEventListener('click', this.handleSubmit);
    this.removeBtn?.removeEventListener('click', this.handleRemove);
  }

  handleTriggerKeydown(event) {
    if (event.code === 'Enter' || event.code === 'Space') {
      event.preventDefault();
      this.handleToggle();
    }
  }

  handleToggle() {
    this.showForm();
    this.input?.focus();
  }

  showForm() {
    this.trigger?.classList.add('hidden');
    this.form?.classList.remove('hidden');
  }

  showApplied(code) {
    this.showForm();
    this.applied?.classList.remove('hidden');
    if (this.appliedText) this.appliedText.textContent = `Cupom ${code} aplicado`;
    this.hideError();
  }

  showTrigger() {
    if (!this.trigger) {
      this.showForm();
      this.applied?.classList.add('hidden');
      if (this.input) this.input.value = '';
      this.hideError();
      return;
    }

    this.trigger.classList.remove('hidden');
    this.form?.classList.add('hidden');
    this.applied?.classList.add('hidden');
    if (this.input) this.input.value = '';
    this.hideError();
  }

  showError(message) {
    this.errorEl.textContent = message;
    this.errorEl.classList.remove('hidden');
  }

  hideError() {
    this.errorEl.textContent = '';
    this.errorEl.classList.add('hidden');
  }

  async fetchCart() {
    const response = await fetch(`${routes.cart_url}.json`);
    if (!response.ok) throw new Error('Failed to fetch cart');
    return response.json();
  }

  hasCartErrors(cart) {
    if (!cart?.errors) return false;
    if (typeof cart.errors === 'string') return cart.errors.length > 0;
    if (Array.isArray(cart.errors)) return cart.errors.length > 0;
    if (typeof cart.errors === 'object') return Object.keys(cart.errors).length > 0;
    return Boolean(cart.errors);
  }

  hasApplicableDiscountCode(cart, code) {
    const normalizedCode = code.toLowerCase();

    return getApplicableDiscountCodes(cart).some((discountCode) => discountCode.toLowerCase() === normalizedCode);
  }

  getAppliedDiscountLabel(cart) {
    const codes = getApplicableDiscountCodes(cart);
    if (codes.length > 0) return codes.join(', ');

    return null;
  }

  wasDiscountApplied(afterCart, code) {
    return this.hasApplicableDiscountCode(afterCart, code);
  }

  async handleSubmit() {
    const code = this.input.value.trim();
    if (!code) return;

    this.submitBtn.disabled = true;
    this.hideError();

    try {
      const beforeCart = await this.fetchCart();
      const existingCodes = getApplicableDiscountCodes(beforeCart);
      const discountPayload = [...new Set([...existingCodes, code])].join(',');

      const response = await fetch(`${routes.cart_update_url}`, {
        ...fetchConfig(),
        body: JSON.stringify({ discount: discountPayload }),
      });

      const cart = await response.json();

      if (!response.ok || this.hasCartErrors(cart)) {
        throw new Error(cart.errors || 'Invalid discount');
      }

      let isApplied = this.wasDiscountApplied(cart, code);

      if (!isApplied) {
        const freshCart = await this.fetchCart();
        isApplied = this.wasDiscountApplied(freshCart, code);
      }

      if (!isApplied) {
        this.showError('Cupom inválido ou expirado');
        return;
      }

      this.input.value = '';
      this.showApplied(getApplicableDiscountCodes(await this.fetchCart()).join(', ') || code);
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-drawer-coupon', cartData: cart });
    } catch (error) {
      this.showError('Cupom inválido ou expirado');
    } finally {
      this.submitBtn.disabled = false;
    }
  }

  async handleRemove() {
    this.removeBtn.disabled = true;

    try {
      const response = await fetch(`${routes.cart_update_url}`, {
        ...fetchConfig(),
        body: JSON.stringify({ discount: '' }),
      });

      const cart = await response.json();

      if (!response.ok || this.hasCartErrors(cart)) {
        throw new Error(cart.errors || 'Failed to remove discount');
      }

      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-drawer-coupon', cartData: cart });
      await this.syncWithCart();
    } catch (error) {
      console.error(error);
    } finally {
      this.removeBtn.disabled = false;
    }
  }

  async syncWithCart() {
    try {
      const cart = await this.fetchCart();
      const label = this.getAppliedDiscountLabel(cart);

      if (label) {
        this.showApplied(label);
      } else if (this.form && !this.form.classList.contains('hidden')) {
        this.showForm();
        this.applied.classList.add('hidden');
      } else {
        this.showTrigger();
      }
    } catch (error) {
      console.error(error);
    }
  }
}

customElements.define('cart-drawer-coupon', CartDrawerCoupon);
